import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@/common/styles/style";
import { styles } from "./style";
import BackButton from "@/atoms/backButton";
import InputPopover from "@/molecules/inputPopover";
import MenuPopover from "@/molecules/menuPopover";
import DeleteConfirmModal from "@/organisms/deleteConfirmModal";
import { TenantsType, UsersType, GroupsType } from "@/common/type/type";
import Users from "./users";
import Groups from "./groups";
import Tenants from "./tenats";
import CircularProgress from "@/molecules/circularProgress";
import { useLoading } from "@/hooks/useLoading";
import { useAuth } from "@/hooks/useAuth";
import { Routes } from "@/router/routes";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "@/atoms/errorMessage";
import { getRoleName, roleInfo } from "@/helpers/getUserInfo";

export default function UserManagement() {
  const navigate = useNavigate();
  const [selectedTenant, setSelectedTenant] = useState<TenantsType | null>(
    null
  );
  const [selectedGroup, setSelectedGroup] = useState<GroupsType | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editValue, setEditValue] = useState<string | number>("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [search, setSearch] = useState({ tenant: "", group: "", user: "" });
  const [isUnassigned, setIsUnassigned] = useState(false);
  const [tenantsList, setTenantsList] = useState<TenantsType[]>([]);
  const [groupsList, setGroupsList] = useState<GroupsType[]>([]);
  const [usersList, setUsersList] = useState<UsersType[]>([]);
  const [unassignedUsers, setUnassignedUsers] = useState<UsersType[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [type, setType] = useState("");
  const [clickId, setClickId] = useState(0);
  const [isUpdated, setIsUpdated] = useState(true);
  const [filteredGroups, setFilteredGroups] =
    useState<GroupsType[]>(groupsList);
  const { loading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState<string | null>(null);
  const { userRole } = useAuth();
  const open = Boolean(anchorEl);

  const resolveType = (name: string) => {
    if (groupsList.some(group => group.name === name)) { 
      return "group";
    }
    if (tenantsList.some(tenant => tenant.name === name)) {
      return "tenant";
    }
    return "";
  };

  useEffect(() => {
    if (!isUpdated) {
      return;
    }

    const checkServerConnection = async () => {
      startLoading();
      try {
        const response = await fetch("/api/user", {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (response.ok) {
          console.log(data.message);
          const tenants = data.result.tenants;
          const groups = data.result.tenants.flatMap(
            (tenant: TenantsType) => tenant.groups
          );
          const users = groups.flatMap((group: GroupsType) => group.users);
          const unassigned = data.result.usersWithoutTenant;

          setTenantsList(tenants);
          setGroupsList(groups);
          setUsersList([...users, ...unassigned]);
          setUnassignedUsers(unassigned);
        } else if (response.status === 404) {
          alert(data.message);
          navigate(Routes.ERROR);
        } else if (response.status === 401 || response.status === 403) {
          alert(data.message);
          navigate(Routes.LOGIN);
        } else {
          setError(data.message);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      } finally {
        stopLoading();
        setIsUpdated(false);
      }
    };

    checkServerConnection();
  }, [isUpdated]);

  useEffect(() => {
    if (selectedTenant && tenantsList.length > 0) {
      const updatedTenant = tenantsList.find(tenant => tenant.id === selectedTenant.id);
      if (updatedTenant) {
        setSelectedTenant(updatedTenant);
      }
    }
  }, [tenantsList]);

  useEffect(() => {
    if (selectedGroup && groupsList.length > 0) {
      const updatedGroup = groupsList.find(group => group.id === selectedGroup.id);
      if (updatedGroup) {
        setSelectedGroup(updatedGroup);
      }
    }
  }, [groupsList]);

  const handleSearch = (searchType: string, value: string) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      [searchType]: value,
    }));
  };

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    popType: string | null
  ) => {
    setAnchorEl(event.currentTarget);
    setContent(popType);
    if (popType === "invite") {
      setEditValue("test@example.com");
    } else if (popType === "edit") {
      setEditValue(selectedItem);
    } else if (popType === "tenant" || popType === "group") {
      setType(popType);
      setEditValue("");
    } else {
      setEditValue("");
    }
  };

  const handleAddSubmit = async (url: string, body: {}) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        setIsUpdated(true);
        console.log(data.message);
        handlePopoverClose();
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  const handleEditSubmit = async (type: string, url: string, body: {}) => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        if (type === "tenant") {
          const updatedTenants = tenantsList?.map((tenant) => {
            if (tenant.name === selectedItem) {
              return { ...tenant, name: editValue as string };
            }
            return tenant;
          });
          setTenantsList(updatedTenants || []);
        } else if (type === "group") {
          const updatedGroups = groupsList?.map((group) => {
            if (group.name === selectedItem) {
              return { ...group, name: editValue as string };
            }
            return group;
          });
          setGroupsList(updatedGroups || []);
        }
        setIsUpdated(true);
        handlePopoverClose();
        handleMenuClose();
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
    }
  };

  const handleSendMail = async (url: string, body: {}) => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        setIsUpdated(true);
        handlePopoverClose();
        handleMenuClose();
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  const handleSubmit = (e: React.FormEvent, type: string) => {
    e.preventDefault();
    let id: number | undefined;
    let url: string = "";
    let body = {};

    if (type === "tenant") {
      id = tenantsList?.find((tenant) => tenant.name === selectedItem)?.id;
      if (content === "tenant") {
        url = `/api/tenant`;
      } else if (content === "invite") {
        url = `/api/tenant/invite/${id}`;
      } else {
        url = `/api/tenant/${id}`;
      }
      body =
        content !== "invite" ? { tenantName: editValue } : { email: editValue };
    } else if (type === "group") {
      id = groupsList?.find((group) => group.name === selectedItem)?.id;
      if (content === "group") {
        url = `/api/group/${clickId}`;
      } else if (content === "invite") {
        url = `/api/group/invite/${id}`;
      } else {
        url = `/api/group/${id}`;
      }
      body =
        content !== "invite" ? { groupName: editValue } : { email: editValue };
    }

    switch (content) {
      case "invite":
        return handleSendMail(url, body);
      case "edit":
        return handleEditSubmit(type, url, body);
      default:
        return handleAddSubmit(url, body);
    }
  };

  const handleDeleteConfirm = async (type: string) => {
    let id: number | undefined;
    let url: string = "";

    if (type === "tenant") {
      id = tenantsList?.find((tenant) => tenant.name === selectedItem)?.id;
      url = `/api/tenant/${id}`;
    } else if (type === "group") {
      id = groupsList?.find((group) => group.name === selectedItem)?.id;
      url = `/api/group/${id}`;
    }

    if (!id || !selectedItem) {
      console.error(
        "削除対象のIDが見つかりませんまたは選択されたアイテムがありません"
      );
      return;
    }

    try {
      const response = await fetch(url, {
        method: "DELETE",
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
        if (selectedItem) {
          if (type === "tenant") {
            const updatedTenants =
              tenantsList?.filter((tenant) => tenant.name !== selectedItem) ||
              [];
            setTenantsList(updatedTenants);
          } else if (type === "group") {
            const updatedGroups =
              groupsList?.filter((group) => group.name !== selectedItem) || [];
            setGroupsList(updatedGroups);
          }
        }
        handlePopoverClose();
        handleMenuClose();
        setDeleteModalOpen(false);
      } else if (response.status === 404) {
        navigate(Routes.ERROR);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
    }
  };

  const handleDeleteClick = (item: string) => {
    setSelectedItem(item);
    setDeleteModalOpen(true);
    setIsUpdated(true);
  };

  const handleLeave = async (type: "tenant" | "group", name: string) => {
    let url: string = "";

    const email = (usersToDisplay || []).find(
      (user) => user?.name === name
    )?.email;

    const userGroups = ((filteredGroups as GroupsType[]) || []).filter(
      (group) =>
        (group.users as UsersType[]).some(
          (groupUser) => groupUser.email === email
        )
    );

    const userId = usersList?.find((user) => user.name === selectedItem)?.id;
    const groupId = userGroups?.length ? userGroups[0].id : undefined;

    if (type === "tenant") {
      url = `/api/tenant/leave/${userId}`;
    } else if (type === "group") {
      url = `/api/group/leave/${userId}/${groupId}`;
    }

    if (!userId || !email) {
      console.error("IDまたはEメールがありません。");
      return;
    }

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message, ", ", email);
        handlePopoverClose();
        setIsUpdated(true);
        handleMenuClose();
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        navigate(Routes.ERROR);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  const updateAdminStatus = async (
    type: "grant" | "remove",
    selectedUser: string
  ) => {
    const selectedUserInfo = usersToDisplay.find(
      (user) => user?.name === selectedUser
    );

    try {
      const response = await fetch(
        `/api/admin/${type}/${selectedUserInfo?.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
      } else {
        console.error("サーバ接続エラー: ", response.statusText);
        navigate(Routes.ERROR);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    name: string
  ) => {
    if (tenantsList?.find((tenant) => tenant.name === name)) {
      setType("tenant");
    } else if (groupsList?.find((group) => group.name === name)) {
      setType("group");
    }
    setMenuAnchorEl(event.currentTarget);
    setSelectedItem(name);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const calculatedGroups = useMemo(() => {
    if (!groupsList) return [];
    if (!selectedTenant) return groupsList.filter((g) => g.id !== 0);
  
    const tenantGroups = selectedTenant.groups.filter((group) => group.id !== 0);
  
    const newGroup =
      groupsList.find((group) => group.name === editValue && group.id !== 0);
  
    const newGroups = newGroup ? [...tenantGroups, newGroup] : tenantGroups;
  
    return newGroups;
  }, [selectedTenant, groupsList, editValue]);

  useEffect(() => {
    setFilteredGroups(calculatedGroups);
  }, [calculatedGroups]);

  const filteredUsers = selectedGroup
    ? selectedGroup.users ?? []
    : selectedTenant
    ? usersList.filter((user) =>
        selectedTenant.groups.some((group) =>
          group.users?.some(
            (groupUser) => groupUser.email === user.email
          )
        )
      )
    : usersList;

  const usersToDisplay = isUnassigned ? unassignedUsers : filteredUsers;

  return (
    <Box sx={styles.container}>
      <Stack direction="row" spacing={3} sx={styles.titleArea}>
        <BackButton />
        <Typography variant="h3" sx={styles.title}>
          ユーザー管理
        </Typography>
      </Stack>
      <ErrorMessage error={error} />
      {loading ? (
        <CircularProgress />
      ) : (
        <Stack direction="row" spacing={5}>
          <Tenants
            handleSearch={handleSearch}
            handlePopoverOpen={handlePopoverOpen}
            setSelectedTenant={setSelectedTenant}
            setSelectedGroup={setSelectedGroup}
            setIsUnassigned={setIsUnassigned}
            handleMenuClick={handleMenuClick}
            setClickId={setClickId}
            tenantsList={tenantsList || []}
            search={search}
            userRole={userRole}
            selectedTenant={selectedTenant}
          />
          <Groups
            handleSearch={handleSearch}
            handlePopoverOpen={handlePopoverOpen}
            setSelectedGroup={setSelectedGroup}
            setIsUnassigned={setIsUnassigned}
            handleMenuClick={handleMenuClick}
            setClickId={setClickId}
            filteredGroups={filteredGroups || []}
            search={search}
            userRole={userRole}
            selectedGroup={selectedGroup}
          />
          <Users
            handleSearch={handleSearch}
            setSelectedTenant={setSelectedTenant}
            setSelectedGroup={setSelectedGroup}
            setIsUnassigned={setIsUnassigned}
            handleMenuClick={handleMenuClick}
            usersToDisplay={usersToDisplay || []}
            filteredGroups={filteredGroups || []}
            search={search}
            userRole={userRole}
          />
        </Stack>
      )}

      <InputPopover
        open={open}
        anchorEl={anchorEl}
        handleEditSubmit={(e) => handleSubmit(e, type)}
        onClose={handlePopoverClose}
        setEditValue={setEditValue}
        editValue={editValue}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        buttonLabel="Submit"
      />

      <MenuPopover
        anchorEl={menuAnchorEl}
        handleMenuClose={handleMenuClose}
        data={
          getRoleName(userRole) === roleInfo.admin &&
          usersToDisplay?.some((user) => user?.name === selectedItem)
            ? [
                {
                  name: "テナント解除",
                  func: () => handleLeave("tenant", selectedItem),
                },
                {
                  name: "グループ解除",
                  func: () => handleLeave("group", selectedItem),
                },
                {
                  name: "管理者権限の付与",
                  func: () => updateAdminStatus("grant", selectedItem),
                },
                {
                  name: "管理者権限の解除",
                  func: () => updateAdminStatus("remove", selectedItem),
                },
              ]
            : usersToDisplay?.some((user) => user?.name === selectedItem)
            ? [
                {
                  name: "テナント解除",
                  func: () => handleLeave("tenant", selectedItem),
                },
                {
                  name: "グループ解除",
                  func: () => handleLeave("group", selectedItem),
                },
              ]
            : [
                {
                  name: `${selectedItem} 招待`,
                  func: (e) => {
                    if (e) {
                      setType(resolveType(selectedItem));
                      handlePopoverOpen(e, "invite");
                    }
                  },
                },
                {
                  name: `${selectedItem} 編集`,
                  func: (e) => {
                    if (e) {
                      setType(resolveType(selectedItem));
                      handlePopoverOpen(e, "edit");
                    }
                  },
                },
                {
                  name: `${selectedItem} 削除`,
                  func: () => {
                    setType(resolveType(selectedItem));
                    handleDeleteClick(selectedItem);
                  }
                },
              ]
        }
      />
      <DeleteConfirmModal
        open={deleteModalOpen}
        deleteMessage={selectedItem}
        handleDelete={() => handleDeleteConfirm(type)}
        onClose={() => setDeleteModalOpen(false)}
      />
    </Box>
  );
}
